import cn from "classnames"
import React, { useEffect, useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Form } from "reactstrap"
import {
    CleanHtml,
    FloatingLabelInput,
    MixedMerchantDetails,
    PageContainer,
    PageWrapper,
    ShowErrorMessages,
    merchantSelectors,
    useSelector,
    valFuncRequired,
} from "swiipe.portal.shared"
import SubmitButton from "../../components/buttons/SubmitButton"
import CancelService from "../../components/merchant/CancelService"
import GoBackContainer from "../../components/navigations/GoBackContainer"
import { SharedCancelContainer } from "../../components/servicesonboarding/SharedCancelContainer"
import { getImage } from "../../services/imageLanguageService"
import { getMerchantContact } from "../../services/merchantService"
import { getProviderStatus } from "../../services/paymentService"
import { useRequiredUserRelations } from "../../services/userRelationService"
import { IMerchantProviderConfig, merchantServicesSelectors } from "../../store/reducers/merchantServicesReducer"
import { StoreState } from "../../store/StoreState"
import {
    IKlarnaOnboardingData,
    getKlarnaOnboardingDataThunk,
    getOnboardingStatusThunk,
    klarnaOnboardingThunk,
    setPiqOnboardingStatusThunk,
} from "../../store/thunks/swiipePaymentsThunks"
import { sendServiceCancellationEmailThunk } from "../../store/thunks/swiipeServicesThunks"
import { useReduxDispatch } from "../../store/useReduxDispatch"
import "./KlarnaOnboardingPage.scss"

interface IKlarnaOnboardingPageProps {
    webshopIds?: string[]
    isEdit?: boolean
    onGoBack: () => void
    onOnboarded: () => void
}
interface IKlarnaOnboardingPageInnerProps extends IKlarnaOnboardingPageProps {
    merchantDetails: MixedMerchantDetails
    selectedWebshopId?: string
}

const KlarnaOnboardingPage = (props: IKlarnaOnboardingPageProps) => {
    useRequiredUserRelations([{ requiredType: "Merchant" }])
    const merchantDetails = useSelector(merchantSelectors.merchantDetails)
    const selectedWebshopId = useSelector(merchantSelectors.selectedWebshopId)

    if (!merchantDetails || !merchantDetails.webshops.length) {
        return null
    }

    return (
        <KlarnaOnboardingPageInner
            merchantDetails={merchantDetails}
            selectedWebshopId={selectedWebshopId}
            {...props}
        ></KlarnaOnboardingPageInner>
    )
}

interface IFormModel {
    klarnaId: string
    klarnaKey: string
}

const KlarnaOnboardingPageInner = ({
    merchantDetails,
    webshopIds,
    selectedWebshopId,
    isEdit,
    onGoBack,
    onOnboarded,
}: IKlarnaOnboardingPageInnerProps) => {
    const { t } = useTranslation()
    const dispatch = useReduxDispatch()

    const [showCancelService, setShowCancelService] = useState(false)

    useEffect(() => {
        merchantDetails?.webshops.forEach(
            async (w) =>
                await dispatch(
                    getOnboardingStatusThunk(merchantDetails?.swMerchant.swMerchantId, w.webshopId, false, [
                        "KlarnaFinancing",
                        "KlarnaPayLater",
                    ])
                )
        )
    }, [merchantDetails])

    useEffect(() => {
        if (isEdit && selectedWebshopId) {
            ;(async () => {
                const response = await dispatch(
                    getKlarnaOnboardingDataThunk(selectedWebshopId, merchantDetails.swMerchant.swMerchantId)
                )

                setValue("klarnaId", response.klarnaId)
                setValue("klarnaKey", Array(17).join("*"))
            })()
        }
    }, [isEdit, selectedWebshopId])

    const onboardingStatuses = useSelector<StoreState, IMerchantProviderConfig | undefined>((state) =>
        merchantServicesSelectors.merchantStatuses(state, merchantDetails.swMerchant.swMerchantId)
    )

    const webshops = merchantDetails.webshops || []

    const { handleSubmit, register, setValue, formState } = useForm<IFormModel>()

    if (!webshopIds && !isEdit) {
        return null
    }

    const onSubmit: SubmitHandler<IFormModel> = async (data) => {
        try {
            const webshopids: string[] = webshopIds && webshopIds.length ? webshopIds : webshops.map((w) => w.webshopId)
            const swMerchantContact = getMerchantContact(merchantDetails)

            const formData: IKlarnaOnboardingData = {
                swMerchantId: merchantDetails.swMerchant.swMerchantId,
                swMerchantEmail: swMerchantContact?.email || "",
                swMerchantName: merchantDetails.swMerchant.name,
                merchantContactName: swMerchantContact?.name || "",
                merchantContactPhone: swMerchantContact?.phone || "",
                webshopIds: webshopids.filter((wid) => {
                    const conf = onboardingStatuses ? onboardingStatuses[wid] : undefined
                    const isActionRequired = (providerStatus?: string) => !providerStatus || providerStatus === "ActionRequired"
                    const payLaterStatus = getProviderStatus(conf?.gatewayProviders, "KlarnaPayLater", "Klarna")
                    const financingStatus = getProviderStatus(conf?.gatewayProviders, "KlarnaFinancing", "Klarna")
                    return isActionRequired(payLaterStatus) && isActionRequired(financingStatus)
                }),
                klarnaId: data.klarnaId,
                klarnaKey: data.klarnaKey,
            }

            await dispatch(klarnaOnboardingThunk(formData, false))

            onOnboarded()
        } catch {
            //Catch to stop showing spinner
        }
    }

    return (
        <>
            {showCancelService && (
                <CancelService
                    serviceName={t("klarnaonboarding.klarna")}
                    cancelText={t("cancelService.klarna.cancelText")}
                    customCancelledText={t("cancelService.klarna.cancelledText")}
                    hintText={t("cancelService.klarna.hintText")}
                    onCancel={async () => {
                        await dispatch(
                            setPiqOnboardingStatusThunk(merchantDetails.swMerchant.swMerchantId, [selectedWebshopId!], {
                                paymentType: "KlarnaPayLater",
                                providerName: "Klarna",
                                providerType: "PspAndAquirer",
                                enabled: false,
                            })
                        )

                        await dispatch(
                            setPiqOnboardingStatusThunk(merchantDetails.swMerchant.swMerchantId, [selectedWebshopId!], {
                                paymentType: "KlarnaFinancing",
                                providerName: "Klarna",
                                providerType: "PspAndAquirer",
                                enabled: false,
                            })
                        )

                        await dispatch(
                            sendServiceCancellationEmailThunk(
                                merchantDetails.swMerchant.swMerchantId,
                                "GeneralServiceCancellationEopEmail",
                                t("serviceNames.klarna"),
                                true
                            )
                        )
                    }}
                    onGoBack={() => setShowCancelService(false)}
                />
            )}
            <PageWrapper className={cn({ "d-none": showCancelService })}>
                {isEdit && <GoBackContainer goBackText={t("commononboardingtexts.backToAllService")} onGoBack={onGoBack} />}
                <PageContainer noHeader={isEdit} disableDefaultPaddingBottom>
                    <div className="klarna-onboarding-container">
                        <div className="klarna-form-head">
                            <img className="swiipe-payments" src={getImage("klarnaLogo")} />
                        </div>

                        {!isEdit && <div className="klarna-text-1">{t("klarnaonboarding.toactivate")}</div>}

                        {!isEdit && (
                            <CleanHtml element="div" className="klarna-text-2" html={t("klarnaonboarding.donthaveone")} />
                        )}

                        <Form onSubmit={handleSubmit(onSubmit)} className="klarna-form">
                            <span className="klarna-id-text">{t("klarnaonboarding.klarnaid")}</span>
                            <FloatingLabelInput
                                name="klarnaId"
                                innerRef={register(valFuncRequired(t("klarnaonboarding.klarnaiderror")))}
                                placeholder=""
                                disabled={isEdit}
                            />
                            <br />
                            <span className="klarna-key-text">{t("klarnaonboarding.klarnakey")}</span>
                            <FloatingLabelInput
                                name="klarnaKey"
                                innerRef={register(valFuncRequired(t("klarnaonboarding.klarnakeyerror")))}
                                placeholder=""
                                disabled={isEdit}
                            />

                            {!isEdit && (
                                <div className="klarna-onboarding-form mt-2">
                                    <ShowErrorMessages formState={formState} />

                                    <SubmitButton
                                        className="mt-4"
                                        dark
                                        noBorder
                                        noBorderRadius
                                        containerClass="submit-btn"
                                        formState={formState}
                                    >
                                        {t("commononboardingtexts.submit")}
                                    </SubmitButton>
                                </div>
                            )}
                        </Form>

                        {!isEdit && (
                            <div className="klarna-back-link mt-3">
                                <a
                                    onClick={() => {
                                        onGoBack()
                                    }}
                                >
                                    {t("commononboardingtexts.back")}
                                </a>
                            </div>
                        )}
                        {isEdit && (
                            <SharedCancelContainer
                                cancelText={t("klarnaonboarding.cancel")}
                                onCancel={() => setShowCancelService(true)}
                            />
                        )}
                    </div>
                </PageContainer>
            </PageWrapper>
        </>
    )
}

export default KlarnaOnboardingPage
